import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2 } from "lucide-react";
import { SyncEntityType } from "@/types/pms";
import { useTriggerSelectiveSync } from "../hooks/pms.hooks";
import {
  getSyncTriggerErrorMessage,
  isBrowserOffline,
} from "../utils/syncTriggerErrors";

const ALL_ENTITIES = Object.values(SyncEntityType);

const ENTITY_LABEL_KEYS: Record<SyncEntityType, string> = {
  [SyncEntityType.HOTEL]: "pms.sync.entityHotel",
  [SyncEntityType.ROOM_TYPE]: "pms.sync.entityRoomType",
  [SyncEntityType.ROOM]: "pms.sync.entityRoom",
  [SyncEntityType.GUEST]: "pms.sync.entityGuest",
  [SyncEntityType.RESERVATION]: "pms.sync.entityReservation",
  [SyncEntityType.PRODUCT]: "pms.sync.entityProduct",
  [SyncEntityType.SERVICE_CATEGORY]: "pms.sync.entityServiceCategory",
  [SyncEntityType.SERVICE]: "pms.sync.entityService",
  [SyncEntityType.RATE_PLAN]: "pms.sync.entityRatePlan",
  [SyncEntityType.RATE]: "pms.sync.entityRate",
  [SyncEntityType.FOLIO]: "pms.sync.entityFolio",
};

interface SelectiveSyncDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const SelectiveSyncDialog: React.FC<SelectiveSyncDialogProps> = ({
  open,
  onOpenChange,
}) => {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<SyncEntityType[]>([...ALL_ENTITIES]);
  const triggerSync = useTriggerSelectiveSync();

  const allSelected = selected.length === ALL_ENTITIES.length;
  const noneSelected = selected.length === 0;

  const handleToggle = (entity: SyncEntityType, checked: boolean) => {
    setSelected((prev) =>
      checked ? [...prev, entity] : prev.filter((e) => e !== entity),
    );
  };

  const handleToggleAll = () => {
    setSelected(allSelected ? [] : [...ALL_ENTITIES]);
  };

  const handleSubmit = () => {
    if (isBrowserOffline()) {
      toast.error(t("pms.sync.networkError"));
      return;
    }

    triggerSync.mutate(selected, {
      onSuccess: () => {
        toast.success(t("pms.sync.triggerSuccess"));
        onOpenChange(false);
        setSelected([...ALL_ENTITIES]);
      },
      onError: (error) => {
        toast.error(getSyncTriggerErrorMessage(error, t));
      },
    });
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(val) => {
        onOpenChange(val);
        if (!val) setSelected([...ALL_ENTITIES]);
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t("pms.sync.selectiveSyncTitle")}</DialogTitle>
          <DialogDescription>
            {t("pms.sync.selectiveSyncDescription")}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3">
          {/* Select all toggle */}
          <label className="flex cursor-pointer items-center gap-2 border-b pb-2">
            <Checkbox checked={allSelected} onCheckedChange={handleToggleAll} />
            <span className="text-sm font-medium">
              {allSelected
                ? t("pms.sync.deselectAll")
                : t("pms.sync.selectAll")}
            </span>
          </label>

          {/* Entity checkboxes */}
          <div className="grid max-h-[300px] grid-cols-1 gap-2 overflow-y-auto sm:grid-cols-2">
            {ALL_ENTITIES.map((entity) => (
              <label
                key={entity}
                className="flex cursor-pointer items-center gap-2 py-1"
              >
                <Checkbox
                  checked={selected.includes(entity)}
                  onCheckedChange={(checked) =>
                    handleToggle(entity, checked as boolean)
                  }
                />
                <span className="text-sm">{t(ENTITY_LABEL_KEYS[entity])}</span>
              </label>
            ))}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={noneSelected || triggerSync.isPending}
          >
            {triggerSync.isPending && (
              <Loader2 className="size-4 animate-spin" />
            )}
            {t("pms.sync.startSync")}
            {!noneSelected && ` (${selected.length})`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
